import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Upload, Camera, Users, MessageCircle, Heart, Share2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface Under13TeamPageProps {
  teamId: number;
  teamName: string;
}

interface TeamPost {
  id: string;
  content: string;
  post_type: string;
  media_url?: string;
  user_id: string;
  created_at: string;
  profiles?: {
    username?: string;
    first_name?: string;
  };
}

interface TeamPhoto {
  id: string;
  photo_url: string;
  caption?: string;
  uploaded_by: string;
  created_at: string;
  profiles?: {
    username?: string;
    first_name?: string;
  };
}

interface Follower {
  id: string;
  follower_id: string;
  following_id: string;
  followed_at: string;
  follower_profile?: {
    username?: string;
    first_name?: string;
  };
  following_profile?: {
    username?: string;
    first_name?: string;
  };
}

export const Under13TeamPage: React.FC<Under13TeamPageProps> = ({ teamId, teamName }) => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<TeamPost[]>([]);
  const [photos, setPhotos] = useState<TeamPhoto[]>([]);
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [newPost, setNewPost] = useState('');
  const [newCaption, setNewCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'posts' | 'photos' | 'followers'>('posts');

  useEffect(() => {
    if (user) {
      loadTeamContent();
    }
  }, [user, teamId]);

  const loadTeamContent = async () => {
    await Promise.all([
      loadPosts(),
      loadPhotos(),
      loadFollowers()
    ]);
  };

  const loadPosts = async () => {
    try {
      // For now, use a simpler approach without the new tables
      // This will be updated once the types are regenerated
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('chat_group_id', `team-${teamId}`)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error loading posts:', error);
        setPosts([]);
        return;
      }
      
      // Transform chat messages to posts format
      const transformedPosts = (data || []).map(msg => ({
        id: msg.message_id,
        content: msg.message,
        post_type: 'text',
        user_id: msg.user_id || '',
        created_at: msg.created_at || new Date().toISOString(),
        profiles: {
          username: 'Team Member',
          first_name: 'Team Member'
        }
      }));
      
      setPosts(transformedPosts);
    } catch (error: any) {
      console.error('Error loading posts:', error);
      setPosts([]);
    }
  };

  const loadPhotos = async () => {
    try {
      // For now, use existing team photos table
      const { data, error } = await supabase
        .from('team_photos')
        .select('*')
        .eq('team_id', teamId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading photos:', error);
        setPhotos([]);
        return;
      }
      
      // Transform data to match expected format
      const transformedPhotos = (data || []).map(photo => ({
        id: photo.id,
        photo_url: photo.photo_url,
        caption: photo.photo_caption || undefined,
        uploaded_by: photo.uploaded_by,
        created_at: photo.created_at,
        profiles: {
          username: 'Team Member',
          first_name: 'Team Member'
        }
      }));
      
      setPhotos(transformedPhotos);
    } catch (error: any) {
      console.error('Error loading photos:', error);
      setPhotos([]);
    }
  };

  const loadFollowers = async () => {
    try {
      // For now, return empty array since this is a new feature
      setFollowers([]);
    } catch (error: any) {
      console.error('Error loading followers:', error);
      setFollowers([]);
    }
  };

  const createPost = async () => {
    if (!newPost.trim() || !user) return;

    setLoading(true);
    try {
      // For now, use chat messages as posts
      const { error } = await supabase
        .from('chat_messages')
        .insert({
          chat_group_id: `team-${teamId}`,
          user_id: user.id,
          message: newPost
        });

      if (error) throw error;

      setNewPost('');
      toast.success('Post created successfully!');
      await loadPosts();
    } catch (error: any) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    setLoading(true);
    try {
      // Upload to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${teamId}/${user.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('team-assets')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('team-assets')
        .getPublicUrl(fileName);

      // Save to database using existing team_photos table
      const { error: dbError } = await supabase
        .from('team_photos')
        .insert({
          team_id: teamId,
          uploaded_by: user.id,
          photo_url: publicUrl,
          photo_caption: newCaption || null
        });

      if (dbError) throw dbError;

      setNewCaption('');
      toast.success('Photo uploaded successfully!');
      await loadPhotos();
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const followUser = async (userId: string) => {
    if (!user || userId === user.id) return;

    try {
      // For now, just show a success message
      // This will be implemented once the new tables are available in types
      toast.success('Following feature coming soon!');
    } catch (error: any) {
      console.error('Error following user:', error);
      toast.error('Failed to follow user: ' + error.message);
    }
  };

  const isFollowing = (userId: string) => {
    return followers.some(f => 
      f.follower_id === user?.id && f.following_id === userId
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {teamName} - Team Space
          </CardTitle>
          <CardDescription>
            Share photos, videos, and connect with your teammates!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-6">
            <Button 
              variant={activeTab === 'posts' ? 'default' : 'outline'}
              onClick={() => setActiveTab('posts')}
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Posts
            </Button>
            <Button 
              variant={activeTab === 'photos' ? 'default' : 'outline'}
              onClick={() => setActiveTab('photos')}
            >
              <Camera className="h-4 w-4 mr-2" />
              Photos
            </Button>
            <Button 
              variant={activeTab === 'followers' ? 'default' : 'outline'}
              onClick={() => setActiveTab('followers')}
            >
              <Heart className="h-4 w-4 mr-2" />
              Following
            </Button>
          </div>

          {activeTab === 'posts' && (
            <div className="space-y-4">
              <div className="space-y-3">
                <Textarea
                  placeholder="Share something with your team..."
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  rows={3}
                />
                <Button 
                  onClick={createPost} 
                  disabled={loading || !newPost.trim()}
                  className="w-full"
                >
                  {loading ? 'Posting...' : 'Share Post'}
                </Button>
              </div>

              <div className="space-y-4">
                {posts.map((post) => (
                  <Card key={post.id}>
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Users className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold">
                              {post.profiles?.username || post.profiles?.first_name || 'Teammate'}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {new Date(post.created_at).toLocaleDateString()}
                            </Badge>
                          </div>
                          <p className="text-sm">{post.content}</p>
                          {post.media_url && (
                            <img 
                              src={post.media_url} 
                              alt="Post media"
                              className="mt-2 rounded-lg max-w-full h-auto"
                            />
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'photos' && (
            <div className="space-y-4">
              <div className="space-y-3">
                <Input
                  placeholder="Add a caption for your photo..."
                  value={newCaption}
                  onChange={(e) => setNewCaption(e.target.value)}
                />
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*,video/*"
                    onChange={uploadPhoto}
                    disabled={loading}
                    className="flex-1"
                  />
                  <Button disabled={loading} size="sm">
                    <Upload className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {photos.map((photo) => (
                  <Card key={photo.id} className="overflow-hidden">
                    <div className="aspect-square relative">
                      <img 
                        src={photo.photo_url} 
                        alt={photo.caption || 'Team photo'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {photo.caption && (
                      <CardContent className="p-2">
                        <p className="text-xs text-muted-foreground">{photo.caption}</p>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'followers' && (
            <div className="space-y-4">
              {followers.map((follow) => (
                <Card key={follow.id}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Users className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-semibold">
                            {follow.follower_profile?.username || follow.follower_profile?.first_name || 'Teammate'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Following {follow.following_profile?.username || follow.following_profile?.first_name || 'Teammate'}
                          </p>
                        </div>
                      </div>
                      {follow.following_id !== user?.id && !isFollowing(follow.following_id) && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => followUser(follow.following_id)}
                        >
                          Follow
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {followers.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No followers yet. Start following your teammates!
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};